import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm, DatePicker, Row, Col, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';
import './css/searchComponent.css';

const { Search } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

const Subscriptions = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const mobileSearchClass = isMobile ? 'custom-search-mobile' : '';
  const [subscriptions, setSubscriptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [installmentModalVisible, setInstallmentModalVisible] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50', '100'] });
  const [installmentPagination, setInstallmentPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [installmentSearch, setInstallmentSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form] = Form.useForm();
  const [installmentForm] = Form.useForm();

  const fetchSubscriptions = async (page = 1, search = '', pageSize = 5) => {
    setLoading(true);
    try {
      const response = await api.get('/subscriptions', {
        params: { page, limit: pageSize, search }
      });
      setSubscriptions(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, current: page, pageSize }));
    } catch (error) {
      message.error('Erro ao carregar assinaturas');
    }
    setLoading(false);
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { limit: 1000 } });
      setCustomers(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar clientes');
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/plans', { params: { limit: 1000 } });
      setPlans(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar planos');
    }
  };

  const fetchInstallments = async (subscriptionId, page = 1, search = '', status = '') => {
    setLoadingInstallments(true);
    try {
      const response = await api.get(`/subscriptions/${subscriptionId}/installments`, {
        params: { page, limit: installmentPagination.pageSize, search, status }
      });
      setInstallments(response.data.data);
      setInstallmentPagination(prev => ({ ...prev, total: response.data.total, current: page }));
    } catch (error) {
      message.error('Erro ao carregar parcelas');
    }
    setLoadingInstallments(false);
  };

  useEffect(() => {
    fetchSubscriptions();
    fetchCustomers();
    fetchPlans();
  }, []);

  const handleTableChange = (pag) => {
    const newPage = pag.pageSize !== pagination.pageSize ? 1 : pag.current;
    fetchSubscriptions(newPage, searchText, pag.pageSize);
  };

  const handleInstallmentTableChange = (pag) => {
    fetchInstallments(selectedSubscription.id, pag.current, installmentSearch, statusFilter);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchSubscriptions(1, value, 5);
  };

  const handleInstallmentSearch = (value) => {
    setInstallmentSearch(value);
    setInstallmentPagination(prev => ({ ...prev, current: 1 }));
    fetchInstallments(selectedSubscription.id, 1, value, statusFilter);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setInstallmentPagination(prev => ({ ...prev, current: 1 }));
    fetchInstallments(selectedSubscription.id, 1, installmentSearch, value);
  };

  const showModal = (subscription = null) => {
    setEditingSubscription(subscription);
    setModalVisible(true);
    if (subscription) {
      form.setFieldsValue({
        ...subscription,
        started_at: subscription.started_at ? dayjs(subscription.started_at) : null,
        payment_at: subscription.payment_at ? dayjs(subscription.payment_at) : null
      });
    } else {
      form.resetFields();
    }
  };

  const showInstallmentModal = (installment = null) => {
    setEditingInstallment(installment);
    setInstallmentModalVisible(true);
    if (installment) {
      installmentForm.setFieldsValue({
        ...installment,
        due_date: installment.due_date ? new Date(installment.due_date) : null
      });
    } else {
      installmentForm.resetFields();
    }
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        started_at: values.started_at ? values.started_at.format('YYYY-MM-DD') : null,
        payment_at: values.payment_at ? values.payment_at.format('YYYY-MM-DD') : null
      };

      if (editingSubscription) {
        await api.put(`/subscriptions/${editingSubscription.id}`, data);
        message.success('Assinatura atualizada com sucesso!');
      } else {
        await api.post('/subscriptions', data);
        message.success('Assinatura criada com sucesso!');
      }
      setModalVisible(false);
      fetchSubscriptions(pagination.current, searchText);
    } catch (error) {
      message.error('Erro ao salvar assinatura');
    }
  };

  const handleInstallmentSubmit = async (values) => {
    try {
      const data = {
        ...values,
        due_date: values.due_date ? values.due_date.toISOString().split('T')[0] : null
      };

      if (editingInstallment) {
        await api.put(`/subscriptions/installments/${editingInstallment.id}`, data);
        message.success('Parcela atualizada com sucesso!');
      } else {
        await api.post(`/subscriptions/${selectedSubscription.id}/installments`, data);
        message.success('Parcela criada com sucesso!');
      }
      setInstallmentModalVisible(false);
      fetchInstallments(selectedSubscription.id, installmentPagination.current, installmentSearch, statusFilter);
    } catch (error) {
      message.error('Erro ao salvar parcela');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/subscriptions/${id}`);
      message.success('Assinatura deletada com sucesso!');
      fetchSubscriptions(pagination.current, searchText);
    } catch (error) {
      message.error('Erro ao deletar assinatura');
    }
  };

  const handleInstallmentDelete = async (id) => {
    try {
      await api.delete(`/subscriptions/installments/${id}`);
      message.success('Parcela deletada com sucesso!');
      fetchInstallments(selectedSubscription.id, installmentPagination.current, installmentSearch, statusFilter);
    } catch (error) {
      message.error('Erro ao deletar parcela');
    }
  };

  const showInstallments = (subscription) => {
    setSelectedSubscription(subscription);
    setInstallmentSearch('');
    setStatusFilter('');
    fetchInstallments(subscription.id);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: (a, b) => a.id - b.id },
    { title: 'Time', dataIndex: 'customer_name', key: 'customer_name', sorter: (a, b) => a.customer_name?.localeCompare(b.customer_name) },
    { title: 'Plano', dataIndex: 'plan_name', key: 'plan_name', sorter: (a, b) => a.plan_name?.localeCompare(b.plan_name) },
    { title: 'Valor', dataIndex: 'plan_price', key: 'plan_price', render: (value) => value ? `R$ ${parseFloat(value).toFixed(2)}` : '', sorter: (a, b) => (a.plan_price || 0) - (b.plan_price || 0) },
    { title: 'Status', dataIndex: 'status', key: 'status', sorter: (a, b) => a.status?.localeCompare(b.status) },
    { title: 'Início', dataIndex: 'started_at', key: 'started_at', render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '' },
    { title: 'Criado em', dataIndex: 'created_at', key: 'created_at', render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '' },
    { title: 'Pago em', dataIndex: 'payment_at', key: 'payment_at', render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '' },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={(e) => {
              e.stopPropagation();
              showModal(record);
            }}
            size="small"
          />
          <Popconfirm
            title="Tem certeza que deseja deletar esta assinatura?"
            onConfirm={(e) => {
              e.stopPropagation();
              handleDelete(record.id);
            }}
          >
            <Button 
              icon={<DeleteOutlined />} 
              danger 
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const installmentColumns = [
    { title: 'Parcela', dataIndex: 'installment', key: 'installment', sorter: (a, b) => a.installment - b.installment },
    { title: 'Status Pagamento', dataIndex: 'payment_status', key: 'payment_status', sorter: (a, b) => a.payment_status?.localeCompare(b.payment_status) },
    { title: 'Criado em', dataIndex: 'created_at', key: 'created_at', render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '' },
    { title: 'Pago em', dataIndex: 'payment_at', key: 'payment_at', render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '' },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => showInstallmentModal(record)}
            size="small"
          />
          <Popconfirm
            title="Tem certeza que deseja deletar esta parcela?"
            onConfirm={() => handleInstallmentDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0,
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <Search
          className={mobileSearchClass}
          placeholder="Buscar assinaturas..."
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={handleSearch}
          style={{ width: isMobile ? '100%' : 300 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          Nova Assinatura
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={subscriptions}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        showSorterTooltip={false}
        size="default"
        onRow={(record) => ({
          onClick: () => showInstallments(record),
          style: { cursor: 'pointer' }
        })}
      />

      {selectedSubscription && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3>Parcelas - {selectedSubscription.customer_name}</h3>
            <Space>
              <Select
                placeholder="Filtrar por status"
                allowClear
                style={{ width: 150 }}
                onChange={handleStatusFilter}
                value={statusFilter || undefined}
              >
                <Option value="PENDING">Pendente</Option>
                <Option value="PAID">Pago</Option>
                <Option value="OVERDUE">Vencido</Option>
                <Option value="CANCELLED">Cancelado</Option>
              </Select>
              <Search
                placeholder="Buscar parcelas..."
                allowClear
                onSearch={handleInstallmentSearch}
                style={{ width: 200 }}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={() => showInstallmentModal()}>
                Nova Parcela
              </Button>
            </Space>
          </div>
          <Table
            columns={installmentColumns}
            dataSource={installments}
            rowKey="id"
            loading={loadingInstallments}
            pagination={installmentPagination}
            onChange={handleInstallmentTableChange}
            size="small"
          />
        </div>
      )}

      <Modal
        title={editingSubscription ? 'Editar Assinatura' : 'Nova Assinatura'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        styles={{
          content: {
            paddingLeft: isMobile ? 0 : 24,
            paddingRight: isMobile ? 0 : 24
          }
        }}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item name="customer_id" label="Time" rules={[{ required: true }]}>
            <Select placeholder="Selecione o time">
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>{customer.game_name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="plan_id" label="Plano" rules={[{ required: true }]}>
            <Select placeholder="Selecione o plano">
              {plans.map(plan => (
                <Option key={plan.id} value={plan.id}>{plan.name} - R$ {parseFloat(plan.price || 0).toFixed(2)}</Option>
              ))}
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="started_at" label="Data Início Assinatura">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select>
                  <Option value="ATIVO">Ativo</Option>
                  <Option value="INATIVO">Inativo</Option>
                  <Option value="CANCELADO">Cancelado</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="payment_at" label="Data Pagamento">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="payment_status" label="Status Pagamento">
                <Select>
                  <Option value="PENDING">Pendente</Option>
                  <Option value="PAID">Pago</Option>
                  <Option value="CANCELED">Cancelado</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{marginTop: 28}}>
            <Space>
              <Button type="primary" htmlType="submit">
                Salvar
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingInstallment ? 'Editar Parcela' : 'Nova Parcela'}
        open={installmentModalVisible}
        onCancel={() => setInstallmentModalVisible(false)}
        footer={null}
        width={500}
        styles={{
          content: {
            paddingLeft: isMobile ? 0 : 24,
            paddingRight: isMobile ? 0 : 24
          }
        }}
      >
        <Form form={installmentForm} onFinish={handleInstallmentSubmit} layout="vertical">
          <Form.Item name="installment" label="Número da Parcela" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item name="payment_status" label="Status Pagamento" rules={[{ required: true }]}>
            <Select>
              <Option value="PENDING">Pendente</Option>
              <Option value="PAID">Pago</Option>
              <Option value="OVERDUE">Vencido</Option>
              <Option value="CANCELLED">Cancelado</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Salvar
              </Button>
              <Button onClick={() => setInstallmentModalVisible(false)}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Subscriptions;